import {IAnimation} from '@fantasmagames/core/src/display/_interfaces/IAnimation';
import {Spine} from '@fantasmagames/core/src/spine/Spine';
import {Updatable} from '@fantasmagames/core/src/update/Updatable';

interface ICompleteCallbackMap {
	[trackIndex: number]: {runOnce: boolean; callback: Function};
}

interface ITrackCompletedMap {
	[trackIndex: number]: {isActive: boolean; isComplete: boolean};
}

export class SpineAnimationMultiTrack extends Updatable implements IAnimation {
	protected _spine: Spine;
	protected _name: string;
	protected _loop: boolean;

	protected _completeCallback: Function;
	protected _completeCallbackMap: ICompleteCallbackMap = {};

	protected _completed: boolean;
	protected _completedTrack: ITrackCompletedMap = {};

	protected _trackNumber: number;
	protected _onceCallback: boolean;

	protected _chainedIdle: boolean;
	protected _chainedIdleAnimation: string;

	constructor(spine: Spine, name?: string, loop?: boolean) {
		super();

		this._name = name;
		this._loop = loop;

		this._spine = spine;
	}

	public setOnComplete(callback: Function): void {
		this._onceCallback = false;
		this._completeCallback = callback;
	}

	public setTrackOnCompleteCallback(trackIndex: number, callback: Function, runOnce: boolean = true): void {
		this._completeCallbackMap[trackIndex] = {callback, runOnce};
	}

	public removeTrackCompleteCallback(trackIndex: number | string): void {
		this._completeCallbackMap[trackIndex] = null;
	}

	public clearAllTrackData(): void {
		this._completeCallbackMap = {};
		this._completedTrack = {};
	}

	public setOnceOnComplete(callback: Function): void {
		this._onceCallback = true;
		this._completeCallback = callback;
	}

	public start(): void {
		if (!this._isPlaying) {
			super.start();

			if (this.name) {
				this.play(this.name, 0, this._loop);
			}
		}

		this._completed = false;
		this._completedTrack[0] = {isComplete: false, isActive: true};

		this.spineState.clearListeners();
		this.spineState.addListener({
			complete: (data: PIXI.spine.core.TrackEntry) => {
				if (this._completedTrack[data.trackIndex] && this._completedTrack[data.trackIndex].isActive) {
					this._completedTrack[data.trackIndex].isComplete = true;
				}

				if (this.isAllTracksComplete()) {
					this._completed = true;
				}
			},
		});

		// initial state.
		if (!this._isPlaying) {
			this.update(0);
		}
	}

	public play(animationName: string, track: number = 1, loop: boolean = true): SpineAnimationMultiTrack {
		if (!this._spine.skeleton) {
			return;
		}
		const trackEntry = this.spineState.setAnimation(track, animationName, loop);
		this.clearCallback();
		this._completedTrack[track] = {isComplete: false, isActive: true};
		this._completed = false;

		return this;
	}

	public playAdd(animationName: string, track: number = 1, loop: boolean = true): SpineAnimationMultiTrack {
		if (!this._spine.skeleton) {
			return;
		}
		this.spineState.addAnimation(track, animationName, loop, 0);
		this._completedTrack[track] = {isComplete: false, isActive: true};
		this._completed = false;

		return this;
	}

	public playEmpty(track: number, mixDuration: number = 0): SpineAnimationMultiTrack {
		if (!this._spine.skeleton) {
			return;
		}
		this.spineState.setEmptyAnimation(track, mixDuration);

		return this;
	}

	public getAnimationNameOnTrack(track: number): string {
		if (this.isSomethingPlayingOnTrack(track)) {
			return this.spineState.getCurrent(track).animation.name;
		}

		return '';
	}

	public isSomethingPlayingOnTrack(track: number): boolean {
		return this.spineState.getCurrent(track) === null ? false : true;
	}

	public stop(): void {
		this._isPlaying = false;

		// super.stop();
	}

	public stopTrack(trackIndex: number, runCompleteCallback: boolean): void {
		this.spineState.setEmptyAnimation(trackIndex, 0);

		if (runCompleteCallback && this._completeCallbackMap[trackIndex] && this._completeCallbackMap[trackIndex].callback) {
			this._completeCallbackMap[trackIndex].callback();
		}
	}

	public forceDestroy(): void {
		this.stop();
		if (this._spine && this._spine.skeleton) {
			this._spine.destroy();
		}
	}

	public update(dt: number): void {
		this._spine.update(dt);

		for (const track in this._completedTrack) {
			if (this._completedTrack.hasOwnProperty(track)) {
				if (this.hasCompleteCallback(track) && this.isTrackActiveAndComplete(track)) {
					this.runCompleteCallbackOnTrack(track);
				}
			}
		}

		if (this._completed) {
			this._completed = false;
			this._completeCallback && this._completeCallback();

			if (this._chainedIdle && this.chainIdleAnimation && this.spineState) {
				this.play(this._chainedIdleAnimation, 0, true);
				this.start();
			}

			if (this._onceCallback) {
				this._onceCallback = false;
				this.clearCallback();
			}
		}
	}

	public clearCallback(): void {
		this._completeCallback = () => {};
	}

	public chainIdleAnimation(name: string): void {
		this._chainedIdle = true;
		this._chainedIdleAnimation = name;
	}

	public unChainIdleAnimation(): void {
		this._chainedIdle = false;
		this._chainedIdleAnimation = null;
	}

	protected isAllTracksComplete(): boolean {
		let complete: boolean = true;

		for (const track in this._completedTrack) {
			if (this._completedTrack.hasOwnProperty(track)) {
				const value = this._completedTrack[track].isComplete;
				complete = complete && value;
			}
		}

		return complete;
	}

	protected trackIsActive(trackIndex: number | string): boolean {
		return this._completedTrack.hasOwnProperty(trackIndex) && this._completedTrack[trackIndex].isActive;
	}

	protected trackIsComplete(trackIndex: number | string): boolean {
		return this._completedTrack.hasOwnProperty(trackIndex) && this._completedTrack[trackIndex].isComplete;
	}

	protected isTrackActiveAndComplete(trackIndex: number | string): boolean {
		return (
			this._completedTrack.hasOwnProperty(trackIndex) &&
			this._completedTrack[trackIndex].isActive &&
			this._completedTrack[trackIndex].isComplete
		);
	}

	protected hasCompleteCallback(track: string): boolean {
		return !!this._completeCallbackMap[track] && !!this._completeCallbackMap[track].callback;
	}

	protected runCompleteCallbackOnTrack(track: string): void {
		const trackData = this._completedTrack[track];

		if (trackData.isActive && trackData.isComplete && this.hasCompleteCallback(track)) {
			this._completeCallbackMap[track].callback();
			if (this._completeCallbackMap[track].runOnce) {
				this._completeCallbackMap[track].callback = null;
				this.removeTrackCompleteCallback(track);
			}
		}
	}

	public get container(): Spine {
		return this._spine;
	}

	public get name(): string {
		return this._name;
	}

	protected get spineState(): PIXI.spine.core.AnimationState {
		return this._spine.state;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/core/src/display/SpineAnimationMultiTrack.ts